import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  Button,
  Space,
  Tag,
  Switch,
  Select,
  Typography,
  Tooltip,
  Modal as AntModal,
  message,
  Empty,
  Card,
  Grid,
  Spin,
  Divider,
} from "antd";
import {
  PlayCircleOutlined,
  StopOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  KeyOutlined,
  ClearOutlined,
  UserAddOutlined,
  EyeOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import HamburgerMenu from "../common/HamburgerMenu";
import {
  ApplicantSearch,
  GetApplicantPassword,
  StartApplicantContainer,
  StopApplicantContainer,
  ApplicantUpdate,
  ApplicantDetails,
  ApplicantDelete,
  UserDetails,
} from "../../services/APIFunctions";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { permissions } from "../../utils/permissions";
import FastVisaMetrics from "../../utils/FastVisaMetrics";

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const Applicants = () => {
  const { t } = useTranslation();
  const { isAuthenticated, trialActive, subscriptionExpired } = useAuth();
  const screens = useBreakpoint();
  const isMobile = !screens.md; // Cards when width < 768px (md breakpoint)
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allRegisteredUsers, setAllRegisteredUsers] = useState([]);
  const [registeredByFilter, setRegisteredByFilter] = useState("");
  const [filterActive, setFilterActive] = useState(true);
  const [filterRunning, setFilterRunning] = useState(false);
  const fastVisaUserId = sessionStorage.getItem("fastVisa_userid");
  const navigate = useNavigate();
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [searchActionLoadingIds, setSearchActionLoadingIds] = useState(
    () => new Set(),
  );
  const searchActionsInProgress = useRef(new Set());

  // Initialize metrics tracker
  const metrics = useMemo(() => new FastVisaMetrics(), []);

  // Use centralized permissions utility
  const canViewAllApplicants = permissions.canViewAllApplicants();
  const canClearStatus = permissions.canClearStatus();

  // Check if user can start searches (trial active OR subscription not expired)
  const canStartSearch = trialActive || !subscriptionExpired;

  // Check if user can manage applicants and redirect accordingly
  useEffect(() => {
    const checkUserPermissions = async () => {
      if (!fastVisaUserId) return;

      try {
        if (!permissions.canManageApplicants()) {
          const applicants = await ApplicantSearch(fastVisaUserId);

          if (applicants && applicants.length > 0) {
            const firstApplicantId = applicants[0].id;
            console.log(
              "[ApplicantsAntD] User cannot manage applicants, redirecting to applicant:",
              firstApplicantId
            );
            navigate(`/view-applicant/${firstApplicantId}`);
          } else {
            console.log(
              "[ApplicantsAntD] User cannot manage applicants, redirecting to create applicant"
            );
            navigate("/applicant-form");
          }
        }
      } catch (error) {
        console.error(
          "[ApplicantsAntD] Error checking user permissions:",
          error
        );
      }
    };

    if (isAuthenticated && fastVisaUserId) {
      checkUserPermissions();
    }
  }, [isAuthenticated, fastVisaUserId, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      document.body.classList.remove("menu-open");
      navigate("/");
      return;
    }

    // Track page view
    metrics.trackPageView();

    if (fastVisaUserId) {
      metrics.setUserId(fastVisaUserId);
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        let response;
        if (canViewAllApplicants) {
          response = await ApplicantSearch(null);
        } else {
          response = await ApplicantSearch(fastVisaUserId);
        }

        if (Array.isArray(response)) {
          setAllRegisteredUsers([
            ...new Set(
              response.map((item) => item.fastVisa_username).filter(Boolean)
            ),
          ]);
        }

        let filteredData = response;
        if (filterActive) {
          filteredData = filteredData.filter(
            (item) => item.applicant_active === true
          );
        }
        if (filterRunning) {
          filteredData = filteredData.filter(
            (item) => item.search_status === "Running"
          );
        }
        if (canViewAllApplicants && registeredByFilter) {
          filteredData = filteredData.filter(
            (item) => item.fastVisa_username === registeredByFilter
          );
        }
        setData(filteredData);
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error(t("errorFetchingData", "Error fetching data"));

        await metrics.trackCustomEvent("error_encountered", {
          page: "applicants_antd",
          error: "fetch_applicants_failed",
          errorMessage: error.message,
          timestamp: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    if (fastVisaUserId) {
      fetchData();
    }
  }, [
    isAuthenticated,
    fastVisaUserId,
    filterActive,
    filterRunning,
    registeredByFilter,
    canViewAllApplicants,
    navigate,
    refreshFlag,
    metrics,
    t,
  ]);

  const handleRegisterApplicant = () => {
    metrics.trackButtonClick("register-applicant-btn", "Register Applicant");
    navigate("/applicant-form");
  };

  const handleEditApplicant = (id) => {
    metrics.trackButtonClick("edit-applicant-btn", "Edit Applicant");
    navigate(`/applicant-form?id=${id}`);
  };

  const handleView = (id) => {
    metrics.trackButtonClick("view-applicant-btn", "View Applicant Details");
    sessionStorage.setItem("applicant_userid", id);
    navigate(`/ViewApplicant`);
  };

  const handleAction = async (id, isActive) => {
    if (searchActionsInProgress.current.has(id)) return;

    searchActionsInProgress.current.add(id);
    setSearchActionLoadingIds((ids) => new Set(ids).add(id));
    const action = isActive === "Stopped" ? "start_search" : "stop_search";

    try {
      metrics.trackButtonClick(
        `${action}-btn`,
        action === "start_search" ? "Start Search" : "Stop Search"
      );

      if (isActive === "Stopped") {
        // Check if user can start searches
        if (!canStartSearch) {
          AntModal.confirm({
            title: t("subscriptionExpired", "Subscription Expired"),
            content: t(
              "renewSubscriptionMessage",
              "Your subscription has expired. Please renew your subscription to continue using the service."
            ),
            okText: t("renewSubscription", "Renew Subscription"),
            onOk: () => {
              navigate("/subscription");
            },
            cancelText: t("cancel", "Cancel"),
            icon: null,
          });

          await metrics.trackCustomEvent("subscription_expired_block", {
            applicantId: id,
            trialActive: trialActive,
            subscriptionExpired: subscriptionExpired,
            timestamp: new Date().toISOString(),
          });

          return;
        }

        let concurrentLimit = parseInt(
          sessionStorage.getItem("concurrent_applicants"),
          10
        );

        if (Number.isNaN(concurrentLimit) || concurrentLimit <= 0) {
          const latestUser = await UserDetails(fastVisaUserId);
          concurrentLimit = parseInt(latestUser?.concurrent_applicants, 10);
          if (!Number.isNaN(concurrentLimit) && concurrentLimit > 0) {
            sessionStorage.setItem(
              "concurrent_applicants",
              String(concurrentLimit)
            );
          }
        }

        if (Number.isNaN(concurrentLimit) || concurrentLimit <= 0) {
          concurrentLimit = 1;
        }

        const applicantsResponse = await ApplicantSearch(fastVisaUserId);
        const runningCount = applicantsResponse.filter(
          (a) => a.search_status === "Running"
        ).length;

        if (runningCount >= concurrentLimit) {
          AntModal.warning({
            title: t("limitReached", "Limit Reached"),
            content: t(
              "concurrentLimitMessage",
              `You have reached your concurrent applicants limit ({limit}).\nTo start a new applicant, please end one of your currently running applicants first.`
            ).replace("{limit}", concurrentLimit),
          });

          await metrics.trackCustomEvent("concurrent_limit_reached", {
            applicantId: id,
            concurrentLimit: concurrentLimit,
            runningCount: runningCount,
            timestamp: new Date().toISOString(),
          });

          return;
        }

        await StartApplicantContainer(id);
        setData((applicants) =>
          applicants.map((applicant) =>
            applicant.id === id
              ? { ...applicant, search_status: "Running" }
              : applicant,
          ),
        );
        message.success(
          t("searchStartedSuccess", "Search started successfully.")
        );

        await metrics.trackCustomEvent("search_started", {
          applicantId: id,
          timestamp: new Date().toISOString(),
        });
      } else {
        await StopApplicantContainer(id);
        setData((applicants) =>
          applicants.map((applicant) =>
            applicant.id === id
              ? { ...applicant, search_status: "Stopped" }
              : applicant,
          ),
        );
        message.success(
          t("searchStoppedSuccess", "Search stopped successfully.")
        );

        await metrics.trackCustomEvent("search_stopped", {
          applicantId: id,
          timestamp: new Date().toISOString(),
        });
      }
      setRefreshFlag((flag) => !flag);
    } catch (error) {
      message.error(t("errorPerformingAction", "Error performing action."));
      console.error(error);

      await metrics.trackCustomEvent("error_encountered", {
        page: "applicants_antd",
        action: isActive === "Stopped" ? "start_search" : "stop_search",
        applicantId: id,
        error: "action_failed",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    } finally {
      searchActionsInProgress.current.delete(id);
      setSearchActionLoadingIds((ids) => {
        const nextIds = new Set(ids);
        nextIds.delete(id);
        return nextIds;
      });
    }
  };

  const handleCopyPassword = async (id) => {
    metrics.trackButtonClick("copy-password-btn", "Copy Password");

    try {
      const response = await GetApplicantPassword(id);
      const password = response.password;
      await navigator.clipboard.writeText(password);
      message.success(t("passwordCopied", "Password copied to clipboard"));

      await metrics.trackCustomEvent("password_copied", {
        applicantId: id,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error copying password:", error);
      message.error(t("errorCopyingPassword", "Error copying password"));

      await metrics.trackCustomEvent("error_encountered", {
        page: "applicants_antd",
        action: "copy_password",
        applicantId: id,
        error: "copy_password_failed",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleCopyEmail = async (id) => {
    metrics.trackButtonClick("copy-email-btn", "Copy Email");

    try {
      const response = await ApplicantDetails(id);
      const email = response.ais_username;
      await navigator.clipboard.writeText(email);
      message.success(t("emailCopied", "Email copied to clipboard"));

      await metrics.trackCustomEvent("email_copied", {
        applicantId: id,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error copying email:", error);
      message.error(t("errorCopyingEmail", "Error copying email"));

      await metrics.trackCustomEvent("error_encountered", {
        page: "applicants_antd",
        action: "copy_email",
        applicantId: id,
        error: "copy_email_failed",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleResetStatus = async (id) => {
    metrics.trackButtonClick("reset-status-btn", "Clear Status");

    try {
      const response = await ApplicantUpdate(id, {
        search_status: "Stopped",
        container_id: null,
        container_start_datetime: null,
      });

      if (response) {
        message.success(
          t("statusClearedSuccess", "Status cleared successfully.")
        );
        setRefreshFlag((flag) => !flag);

        await metrics.trackCustomEvent("status_reset", {
          applicantId: id,
          timestamp: new Date().toISOString(),
        });
      } else {
        message.error(t("failedToClearStatus", "Failed to clear status."));

        await metrics.trackCustomEvent("error_encountered", {
          page: "applicants_antd",
          action: "reset_status",
          applicantId: id,
          error: "reset_status_failed",
          timestamp: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error clearing status:", error);
      message.error(t("errorClearingStatus", "Error clearing status."));

      await metrics.trackCustomEvent("error_encountered", {
        page: "applicants_antd",
        action: "reset_status",
        applicantId: id,
        error: "reset_status_exception",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDeleteApplicant = async (id) => {
    metrics.trackButtonClick("delete-applicant-btn", "Delete Applicant");

    AntModal.confirm({
      title: t("deleteApplicant", "Delete Applicant"),
      content: t(
        "deleteApplicantConfirm",
        "Are you sure you want to delete this applicant?"
      ),
      okText: t("yes", "Yes"),
      okType: "danger",
      cancelText: t("no", "No"),
      onOk: async () => {
        try {
          const applicantData = await ApplicantDetails(id);
          const isSearchRunning =
            applicantData &&
            (applicantData.search_status === "Running" ||
              applicantData.search_active === true);

          if (isSearchRunning) {
            AntModal.confirm({
              title: t("searchInProgress", "Search in Progress"),
              content: t(
                "stopSearchBeforeDeleting",
                "Search is in progress. Do you want to stop the search before deleting?"
              ),
              okText: t("yes", "Yes"),
              cancelText: t("no", "No"),
              onOk: async () => {
                try {
                  await StopApplicantContainer(id);
                  const response = await ApplicantDelete(id);

                  if (response && response.success) {
                    message.success(
                      t(
                        "applicantDeletedSuccessfully",
                        "Applicant deleted successfully."
                      )
                    );
                    setRefreshFlag((flag) => !flag);

                    await metrics.trackCustomEvent("applicant_deleted", {
                      applicantId: id,
                      searchWasRunning: true,
                      timestamp: new Date().toISOString(),
                    });
                  } else {
                    message.error(
                      t(
                        "unexpectedResponseDeleting",
                        "Unexpected response when deleting applicant."
                      )
                    );
                  }
                } catch (error) {
                  console.error("Error stopping search or deleting:", error);
                  message.error(
                    t(
                      "failedToStopSearchOrDelete",
                      "Failed to stop search or delete applicant. Please try again."
                    )
                  );
                }
              },
            });
          } else {
            try {
              const response = await ApplicantDelete(id);

              if (response && response.success) {
                message.success(
                  t(
                    "applicantDeletedSuccessfully",
                    "Applicant deleted successfully."
                  )
                );
                setRefreshFlag((flag) => !flag);

                await metrics.trackCustomEvent("applicant_deleted", {
                  applicantId: id,
                  searchWasRunning: false,
                  timestamp: new Date().toISOString(),
                });
              } else {
                message.error(
                  t(
                    "unexpectedResponseDeleting",
                    "Unexpected response when deleting applicant."
                  )
                );
              }
            } catch (error) {
              console.error("Error in ApplicantDelete:", error);
              message.error(
                t("errorDeletingApplicant", "Error deleting applicant.")
              );
            }
          }
        } catch (error) {
          console.error("Error checking applicant status:", error);
          message.error(
            t("errorDeletingApplicant", "Error deleting applicant.")
          );
        }
      },
    });
  };

  const getSearchStatusColor = (status) => {
    if (!status) return "default";
    const colors = {
      Running: "#2C6BA0", // Primary color
      Stopped: "#ff9800", // Warning color
      Error: "#f44336", // Error color
      Completed: "#4caf50", // Success color
    };
    return colors[status] || "default";
  };

  const columns = [
    {
      title: <div>{t("aisId", "AIS ID")}</div>,
      dataIndex: "ais_schedule_id",
      key: "ais_schedule_id",
      render: (text, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleView(record.id)}
            icon={<EyeOutlined />}
            style={{ padding: 0 }}
          >
            {text}
          </Button>
          <Tooltip title={t("edit", "Edit")}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditApplicant(record.id)}
              size="small"
              style={{
                opacity: hoveredRowId === record.id ? 1 : 0,
                transition: "opacity 0.2s ease-in",
                pointerEvents: hoveredRowId === record.id ? "auto" : "none",
              }}
            />
          </Tooltip>
          <Tooltip title={t("deleteApplicant", "Delete Applicant")}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteApplicant(record.id)}
              size="small"
              style={{
                opacity: hoveredRowId === record.id ? 1 : 0,
                transition: "opacity 0.2s ease-in",
                pointerEvents: hoveredRowId === record.id ? "auto" : "none",
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: <div>{t("aisUsername", "AIS Username")}</div>,
      dataIndex: "ais_username",
      key: "ais_username",
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          <Tooltip title={t("copyEmail", "Copy Email")}>
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleCopyEmail(record.id)}
              size="small"
              style={{
                opacity: hoveredRowId === record.id ? 1 : 0,
                transition: "opacity 0.2s ease-in",
                pointerEvents: hoveredRowId === record.id ? "auto" : "none",
              }}
            />
          </Tooltip>
          <Tooltip title={t("copyPassword", "Copy Password")}>
            <Button
              type="text"
              icon={<KeyOutlined />}
              onClick={() => handleCopyPassword(record.id)}
              size="small"
              style={{
                opacity: hoveredRowId === record.id ? 1 : 0,
                transition: "opacity 0.2s ease-in",
                pointerEvents: hoveredRowId === record.id ? "auto" : "none",
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: <div>{t("name", "Name")}</div>,
      dataIndex: "name",
      key: "name",
    },
    {
      title: <div>{t("applicantStatus", "Applicant Status")}</div>,
      dataIndex: "applicant_active",
      key: "applicant_active",
      render: (active) => (
        <Tag color={active ? "#4caf50" : "default"}>
          {active ? t("active", "Active") : t("inactive", "Inactive")}
        </Tag>
      ),
    },
    {
      title: <div>{t("searchStatus", "Search Status")} </div>,
      dataIndex: "search_status",
      key: "search_status",
      render: (status, record) => (
        <Space>
          <Tag color={getSearchStatusColor(status)}>
            {t(status ? status.toLowerCase() : "unknown", status || "Unknown")}
          </Tag>
          <Tooltip
            title={
              status === "Stopped"
                ? t("startSearch", "Start Search")
                : t("stopSearch", "Stop Search")
            }
          >
            <Button
              type="text"
              loading={searchActionLoadingIds.has(record.id)}
              icon={
                status === "Stopped" ? <PlayCircleOutlined /> : <StopOutlined />
              }
              onClick={() => handleAction(record.id, status)}
              size="small"
              style={{
                opacity: hoveredRowId === record.id ? 1 : 0,
                transition: "opacity 0.2s ease-in",
                pointerEvents: hoveredRowId === record.id ? "auto" : "none",
              }}
            />
          </Tooltip>
          {canClearStatus && (
            <Tooltip title={t("clearStatus", "Clear Status")}>
              <Button
                type="text"
                icon={<ClearOutlined />}
                onClick={() => handleResetStatus(record.id)}
                size="small"
                style={{
                  opacity: hoveredRowId === record.id ? 1 : 0,
                  transition: "opacity 0.2s ease-in",
                  pointerEvents: hoveredRowId === record.id ? "auto" : "none",
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: <div>{t("targetEndDate", "Target End Date")}</div>,
      dataIndex: "target_end_date",
      key: "target_end_date",
    },
  ];

  // Add Registered By column if user can view all applicants
  if (canViewAllApplicants) {
    columns.push({
      title: <div>{t("registeredBy", "Registered By")}</div>,
      dataIndex: "fastVisa_username",
      key: "fastVisa_username",
    });
  }

  const emptyDescription = (
    <span>
      {t("noApplicantsFound", "No applicants found")}
      <br />
      <small>
        {filterActive || filterRunning || registeredByFilter
          ? t(
              "adjustFilters",
              "Try adjusting your filters or register a new applicant to get started."
            )
          : t(
              "clickToAddFirst",
              "Click 'Register Applicant' to add your first applicant."
            )}
      </small>
    </span>
  );

  const renderMobileCard = (record) => (
    <Card
      key={record.id}
      size="small"
      style={{
        marginBottom: 16,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        borderRadius: 8,
      }}
      styles={{
        body: { padding: "16px" },
      }}
    >
      {/* Header: name as primary identifier, AIS ID as secondary; tap header to view */}
      <div
        role="button"
        tabIndex={0}
        aria-label={t("clickToViewDetails", "Click to view details")}
        onClick={() => handleView(record.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleView(record.id);
          }
        }}
        style={{
          cursor: "pointer",
          marginBottom: 12,
          paddingBottom: 12,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography.Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          {record.name || "—"}
        </Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: "0.8125rem" }}>
          {t("aisId", "AIS ID")}: {record?.ais_schedule_id || "—"}
        </Typography.Text>
      </div>

      {/* Identity: username + copy actions */}
      <div style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary" style={{ fontSize: "0.75rem", display: "block", marginBottom: 4 }}>
          {t("aisUsername", "AIS Username")}
        </Typography.Text>
        <Space align="center" wrap>
          <Typography.Text ellipsis style={{ maxWidth: "100%" }}>
            {record.ais_username || "—"}
          </Typography.Text>
          <Space size={4}>
            <Tooltip title={t("copyEmail", "Copy Email")}>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined style={{ fontSize: 16 }}/>}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyEmail(record.id);
                }}
              />
            </Tooltip>
            <Tooltip title={t("copyPassword", "Copy Password")}>
              <Button
                type="text"
                size="small"
                icon={<KeyOutlined style={{ fontSize: 16 }}/>}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyPassword(record.id);
                }}
              />
            </Tooltip>
          </Space>
        </Space>
      </div>

      {/* Status: tags + optional dates */}
      <div style={{ marginBottom: 12 }}>
        <Space size={[6, 6]} wrap>
          <Tag color={record.applicant_active ? "#4caf50" : "default"}>
            {record.applicant_active ? t("active", "Active") : t("inactive", "Inactive")}
          </Tag>
          <Tag color={getSearchStatusColor(record.search_status)}>
            {t(
              record.search_status ? record.search_status.toLowerCase() : "unknown",
              record.search_status || "Unknown"
            )}
          </Tag>
        </Space>
        {(record.target_end_date || (canViewAllApplicants && record.fastVisa_username)) && (
          <div style={{ marginTop: 8 }}>
            {record.target_end_date && (
              <Typography.Text type="secondary" style={{ fontSize: "0.8125rem", display: "block" }}>
                {t("targetEndDate", "Target End Date")}: {record.target_end_date}
              </Typography.Text>
            )}
            {canViewAllApplicants && record.fastVisa_username && (
              <Typography.Text type="secondary" style={{ fontSize: "0.8125rem", display: "block" }}>
                {t("registeredBy", "Registered By")}: {record.fastVisa_username}
              </Typography.Text>
            )}
          </div>
        )}
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {/* Search actions: primary task */}
      <div style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary" style={{ fontSize: "0.75rem", display: "block", marginBottom: 8 }}>
          {t("searchStatus", "Search Status")}
        </Typography.Text>
        <Space size={8} wrap>
          <Button
            type={record.search_status === "Stopped" ? "primary" : "default"}
            loading={searchActionLoadingIds.has(record.id)}
            icon={
              record.search_status === "Stopped" ? <PlayCircleOutlined /> : <StopOutlined />
            }
            onClick={() => handleAction(record.id, record.search_status)}
            size="middle"
          >
            {record.search_status === "Stopped"
              ? t("startSearch", "Start Search")
              : t("stopSearch", "Stop Search")}
          </Button>
          {canClearStatus && (
            <Button
              type="default"
              icon={<ClearOutlined />}
              onClick={() => handleResetStatus(record.id)}
              size="middle"
            >
              {t("clearStatus", "Clear Status")}
            </Button>
          )}
        </Space>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {/* Primary actions: View, Edit, Delete - icons only on mobile */}
      <Space size={8} wrap style={{ width: "100%", justifyContent: "center" }}>
        <Tooltip title={t("view", "View")}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleView(record.id)}
            size="middle"
          />
        </Tooltip>
        <Tooltip title={t("edit", "Edit")}>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEditApplicant(record.id)}
            size="middle"
          />
        </Tooltip>
        <Tooltip title={t("deleteApplicant", "Delete Applicant")}>
          <Button
            type="default"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteApplicant(record.id)}
            size="middle"
          />
        </Tooltip>
      </Space>
    </Card>
  );

  return (
    <>
      <HamburgerMenu />
      <div
        style={{
          padding: "120px 18px 0px 18px",
          margin: "0 auto",
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        <Card
          bordered={false}
          style={{
            marginBottom: 24,
            boxShadow:
              "0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02)",
            maxWidth: "1580px",
            width: "100%",
            margin: "0 auto",
            padding: isMobile ? "12px" : "0px",
            boxSizing: "border-box",
            overflowX: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div>
              <Title
                level={2}
                style={{ marginBottom: 4, marginTop: 0, color: "#2C6BA0" }}
              >
                <UsergroupAddOutlined style={{ marginRight: 8 }} />
                {t("applicants", "Applicants")}
              </Title>
            </div>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleRegisterApplicant}
              size="large"
            >
              {t("registerApplicant", "Register Applicant")}
            </Button>
          </div>
          <Space
            style={{
              marginTop: 20,
              marginBottom: 20,
              width: isMobile ? "100%" : undefined,
            }}
            size="large"
            wrap
            orientation={isMobile ? "vertical" : "horizontal"}
          >
            <Space>
              {!isMobile && (
                <span style={{ fontWeight: 500 }}>
                  {t("onlyActive", "Only Active")}:
                </span>
              )}
              <Switch checked={filterActive} onChange={setFilterActive} />
              {isMobile && (
                <span style={{ fontWeight: 500 }}>
                  {t("onlyActive", "Only Active")}
                </span>
              )}
            </Space>

            <Space>
              {!isMobile && (
                <span style={{ fontWeight: 500 }}>
                  {t("onlyRunning", "Only Running")}:
                </span>
              )}
              <Switch checked={filterRunning} onChange={setFilterRunning} />
              {isMobile && (
                <span style={{ fontWeight: 500 }}>
                  {t("onlyRunning", "Only Running")}
                </span>
              )}
            </Space>

            {canViewAllApplicants && allRegisteredUsers.length > 0 && (
              <Space orientation={isMobile ? "vertical" : "horizontal"} style={{ width: isMobile ? "100%" : undefined }}>
                <span style={{ fontWeight: 500 }}>
                  {t("registeredBy", "Registered by")}:
                </span>
                <Select
                  value={registeredByFilter}
                  onChange={setRegisteredByFilter}
                  style={{
                    width: isMobile ? "100%" : 260,
                    maxWidth: 260,
                  }}
                  placeholder={t("all", "All")}
                  allowClear
                >
                  <Option value="">{t("all", "All")}</Option>
                  {allRegisteredUsers.map((username) => (
                    <Option key={username} value={username}>
                      {username}
                    </Option>
                  ))}
                </Select>
              </Space>
            )}
          </Space>
          {isMobile ? (
            loading ? (
              <div style={{ padding: "48px 0", textAlign: "center" }}>
                <Spin size="large" tip={t("loading", "Loading...")} />
              </div>
            ) : data.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={emptyDescription}
              />
            ) : (
              <Space orientation="vertical" size={0} style={{ width: "100%" }}>
                {data.map((record) => renderMobileCard(record))}
              </Space>
            )
          ) : (
            <Table
              columns={columns}
              dataSource={data}
              loading={loading}
              rowKey="id"
              tableLayout="auto"
              onRow={(record, index) => ({
                style: {
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#dbecfa1a",
                  transition: "background-color 0.2s ease",
                },
                onMouseEnter: () => setHoveredRowId(record.id),
                onMouseLeave: () => setHoveredRowId(null),
              })}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={emptyDescription}
                  />
                ),
              }}
              scroll={{ x: 1000 }}
            />
          )}
        </Card>
      </div>
    </>
  );
};

export default Applicants;
